import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, X } from "@phosphor-icons/react";
import { useLang } from "@/context/LangContext";

/**
 * WelcomePopup — first-visit modal explaining the freemium offer.
 *
 * Shows once per browser (localStorage flag), on first landing-page
 * visit. Dismissed via the ✕ button, the "Close" text link, backdrop
 * click, or the "Start Free" CTA (which also navigates to /register).
 *
 * Deliberately keeps the homepage itself untouched — the offer copy
 * lives ONLY here so the visible landing page layout is not disturbed.
 * Feb 2026 conversion fix.
 */
const STORAGE_KEY = "esg_welcome_popup_dismissed_v1";

export default function WelcomePopup() {
  const navigate = useNavigate();
  const { t, isRtl } = useLang();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Show on every mount / page refresh — no persistence.
    // A small delay lets the hero paint first so the popup doesn't feel
    // aggressive.
    const timer = setTimeout(() => setOpen(true), 700);
    return () => clearTimeout(timer);
  }, []);

  const close = () => {
    setOpen(false);
  };

  const goRegister = () => {
    close();
    navigate("/register");
  };

  if (!open) return null;

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/75 backdrop-blur-sm"
      data-testid="welcome-popup-backdrop"
      onClick={close}
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-[#0F1013] border border-[#F97316]/30 rounded-2xl max-w-lg w-full p-6 sm:p-8 shadow-2xl max-h-[90vh] overflow-y-auto"
        data-testid="welcome-popup"
      >
        <button
          onClick={close}
          data-testid="welcome-popup-close"
          className={`absolute top-3 ${isRtl ? "left-3" : "right-3"} text-slate-400 hover:text-white transition-colors p-1.5 rounded-full hover:bg-white/5`}
          aria-label={t("popup_close")}
        >
          <X size={22} weight="bold" />
        </button>

        <div className="flex items-center justify-center gap-2 bg-[#F97316]/15 border border-[#F97316]/40 rounded-full px-4 py-2 mb-5 w-full">
          <span
            style={{
              fontSize: 'clamp(8px, 2.3vw, 13.5px)',
              letterSpacing: 'clamp(0.5px, 0.35vw, 1.8px)',
            }}
            className="font-['Outfit'] font-bold text-[#F97316] whitespace-nowrap leading-tight">
            {t("hero_badge")}
          </span>
        </div>

        <p className="text-base sm:text-lg text-white font-['Outfit'] font-bold mb-4" data-testid="welcome-free-intro">
          {t("hero_free_intro")}
        </p>
        <ul className="space-y-2.5 mb-5">
          {[t("hero_free_1"), t("hero_free_2"), t("hero_free_3")].map((line, i) => (
            <li
              key={`free-${i}`}
              className="flex gap-2 text-sm text-slate-300 leading-relaxed"
              data-testid={`welcome-free-bullet-${i + 1}`}
            >
              <CheckCircle size={17} weight="fill" className="text-[#01AF9B] flex-shrink-0 mt-0.5" />
              <span>{line}</span>
            </li>
          ))}
        </ul>

        <p className="text-base sm:text-lg text-white font-['Outfit'] font-bold mb-3" data-testid="welcome-unlock-intro">
          {t("hero_unlock_intro")}
        </p>
        <ul className="space-y-2.5 mb-5">
          {[t("hero_unlock_1"), t("hero_unlock_2"), t("hero_unlock_3")].map((line, i) => (
            <li
              key={`unlock-${i}`}
              className="flex gap-2 text-sm text-slate-300 leading-relaxed"
              data-testid={`welcome-unlock-bullet-${i + 1}`}
            >
              <CheckCircle size={17} weight="fill" className="text-[#F97316] flex-shrink-0 mt-0.5" />
              <span>{line}</span>
            </li>
          ))}
        </ul>

        <p className="text-xs sm:text-sm text-slate-300 leading-relaxed mb-5 pt-3 border-t border-slate-800/60" data-testid="welcome-price-line">
          {t("hero_price_line")}
        </p>

        <button
          onClick={goRegister}
          data-testid="welcome-popup-cta"
          className="w-full bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-bold text-base py-3 px-6 rounded-xl transition-all"
        >
          {t("popup_start_free")}
        </button>

        <p className="mt-3 text-sm sm:text-base font-['Outfit'] font-bold text-[#FBBF24] text-center flex items-center gap-1.5 justify-center" data-testid="welcome-reassurance">
          <CheckCircle size={16} weight="fill" className="text-[#FBBF24] flex-shrink-0" />
          {t("hero_reassurance")}
        </p>
      </div>
    </div>
  );
}
